import { RECEPTION_DIAL } from "../offers/offersCopy";

/**
 * Every word the guide screen says on its own behalf (`TV-4`).
 *
 * The programme titles are the catalogue's; this is the chrome around them —
 * the now/next labels, the sentence an unmapped channel needs, the failure
 * lines. Authored by hand for the reason `offersCopy` gives: there are perhaps
 * thirty strings, they are read at three metres by a guest with a remote, and a
 * machine translation of "Ingen tablå för den här kanalen" reads as a different
 * product.
 *
 * Two locales, because those are the two Foyer ships hotels in today. An
 * unknown locale gets English rather than a blank screen.
 *
 * **Nothing here says "något gick fel".** Every failure sentence names what
 * happened and what to do about it — the rule `RECEPTION_DIAL` was written for,
 * imported rather than re-stated so the house number cannot drift between two
 * screens.
 */

export type GuideLocale = "sv" | "en";

export interface GuideCopy {
  /** Heading, when the premise has not renamed the screen. */
  title: string;
  /** The line-up has not answered yet. */
  loading: string;
  /** It answered, and this room has no channels at all. */
  empty: string;
  /** Kickers on the channel bar. */
  now: string;
  next: string;
  /** The word before a channel number. */
  channel: string;
  /** Heading over the channel list. */
  listTitle: string;
  /* -- the guide's own holes (BE-22) -------------------------------------- */
  /** No event covers this instant, but the channel does have a schedule. */
  noProgrammeNow: string;
  /** …and how long the hole is. */
  gapFor: (minutes: number) => string;
  /** The channel has no schedule at all. */
  noGuide: string;
  /**
   * …and WHY, plus the fact that matters most to a guest: it still plays.
   *
   * An unmapped channel is `200 []` upstream and therefore silent today — the
   * open half of `BE-22`. A guest reading a blank row concludes the television
   * is broken; this sentence is the whole point of surfacing `gaps[]`.
   */
  noGuideWhy: string;
  /** There is nothing after the current programme in the window we hold. */
  guideEnds: string;
  /* -- playback ------------------------------------------------------------ */
  /** Over the poster while a channel is coming up. */
  tuning: string;
  /** A channel in the line-up that this television cannot play. */
  notTunable: string;
  /** It never started, and something has to say so. */
  tuneTimeout: string;
  /** Typed digits that name no channel. */
  noSuchChannel: (typed: string) => string;
  /** Remote hints along the foot. */
  hint: {
    zap: string;
    guide: string;
    watch: string;
    detail: string;
    back: string;
    digits: string;
    last: string;
  };
}

const SV: GuideCopy = {
  title: "TV",
  loading: "Hämtar kanaler…",
  empty: "Inga kanaler i det här rummet.",
  now: "Nu",
  next: "Härnäst",
  channel: "Kanal",
  listTitle: "Kanaler",
  noProgrammeNow: "Ingen tablå just nu",
  gapFor: (m) =>
    m >= 120
      ? `Tablån saknas i ${Math.round(m / 60)} timmar`
      : m >= 60
        ? "Tablån saknas i en timme"
        : `Tablån saknas i ${Math.max(1, m)} min`,
  noGuide: "Ingen tablå för den här kanalen",
  noGuideWhy: "Kanalen saknar tablåkälla. Den går att titta på ändå.",
  guideEnds: "Tablån slutar här",
  tuning: "Byter kanal…",
  notTunable: "Den här kanalen går inte att spela på den här TV:n.",
  tuneTimeout: `Kanalen startade inte. Prova en annan, eller ring receptionen på ${RECEPTION_DIAL}.`,
  noSuchChannel: (typed) => `Ingen kanal ${typed}`,
  hint: {
    zap: "Byt kanal",
    guide: "Kanaler",
    watch: "Titta",
    detail: "Om programmet",
    back: "Tillbaka",
    digits: "Kanalnummer",
    last: "Förra kanalen",
  },
};

const EN: GuideCopy = {
  title: "TV",
  loading: "Loading channels…",
  empty: "No channels in this room.",
  now: "Now",
  next: "Up next",
  channel: "Channel",
  listTitle: "Channels",
  noProgrammeNow: "No programme information right now",
  gapFor: (m) =>
    m >= 120
      ? `Guide missing for ${Math.round(m / 60)} hours`
      : m >= 60
        ? "Guide missing for an hour"
        : `Guide missing for ${Math.max(1, m)} min`,
  noGuide: "No guide for this channel",
  noGuideWhy: "This channel has no guide source. You can still watch it.",
  guideEnds: "The guide ends here",
  tuning: "Changing channel…",
  notTunable: "This channel cannot be played on this television.",
  tuneTimeout: `The channel did not start. Try another, or ring reception on ${RECEPTION_DIAL}.`,
  noSuchChannel: (typed) => `No channel ${typed}`,
  hint: {
    zap: "Change channel",
    guide: "Channels",
    watch: "Watch",
    detail: "About this programme",
    back: "Back",
    digits: "Channel number",
    last: "Last channel",
  },
};

export const GUIDE_COPY: Record<GuideLocale, GuideCopy> = { sv: SV, en: EN };

/**
 * The copy pack for a display locale. Matches on the language subtag alone
 * (`sv-SE` → `sv`), same rule as `offersCopy`.
 */
export function guideCopy(locale: string | undefined): GuideCopy {
  const lang = (locale ?? "").toLowerCase().split(/[-_]/)[0];
  return lang === "sv" ? SV : EN;
}
